/**
 * useRealtimeSync — mounts the realtime "signal-to-pull" client and bridges peer
 * store-change signals to the app's existing sync trigger.
 *
 * The connection follows auth/store/device identity from the app store. The wake
 * callback is held in a ref so a changing `triggerSync` identity never churns the
 * socket — only a real store/device change reconnects. On logout (no token) the
 * connection is torn down.
 */
import { useEffect, useRef, useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useAppStore } from '../store/useAppStore';
import { connectRealtime, disconnectRealtime } from './realtime';

export function useRealtimeSync(triggerSync: () => void) {
    const { authToken, activeStoreId, deviceId } = useAppStore(
        useShallow((s) => ({
            authToken: s.authToken,
            activeStoreId: s.activeStoreId,
            deviceId: s.deviceId,
        })),
    );

    // Keep the latest trigger without re-opening the socket.
    const triggerRef = useRef(triggerSync);
    triggerRef.current = triggerSync;
    const stableWake = useCallback(() => triggerRef.current(), []);

    useEffect(() => {
        if (!authToken || !activeStoreId) {
            disconnectRealtime();
            return;
        }
        connectRealtime({
            token: authToken,
            storeId: activeStoreId,
            deviceId: deviceId ?? null,
            onWake: stableWake,
        });
    }, [authToken, activeStoreId, deviceId, stableWake]);

    // Tear down on unmount.
    useEffect(() => () => disconnectRealtime(), []);
}
